import { useEffect, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '@agms/ui'
import { ticketTypeApi, type TicketCategory, type TicketPriority, type TicketSlaType, type TicketType, type TicketTypePayload, type UserRole } from '../lib/api'
import { usePermissions } from '../hooks/useAuth'
import { apiErrorMessage, apiValidationErrors } from '../hooks/useErrorHandler'
import { PageHeader, SurfaceCard } from '../components/ui'

type ChecklistDraft = {
  text: string
  comment_required_to_complete: boolean
  file_required_to_complete: boolean
}

const ROLE_OPTIONS: Array<{ value: UserRole | 'all'; label: string }> = [
  { value: 'all', label: 'All ticket users' },
  { value: 'support', label: 'Support' },
  { value: 'operations', label: 'Operations' },
  { value: 'management', label: 'Management' },
  { value: 'super_admin', label: 'Super admin' },
]

function initialChecklist(ticketType?: TicketType): ChecklistDraft[] {
  return (ticketType?.checklist_items ?? []).map((item) => ({
    text: item.text,
    comment_required_to_complete: item.comment_required_to_complete,
    file_required_to_complete: item.file_required_to_complete,
  }))
}

function payloadFromForm(form: HTMLFormElement, checklist: ChecklistDraft[], roles: Array<UserRole | 'all'>): TicketTypePayload {
  const data = new FormData(form)
  const defaultSlaType = String(data.get('default_sla_type') ?? '1_day') as TicketSlaType

  return {
    name: String(data.get('name') ?? '').trim(),
    type_group: String(data.get('type_group') ?? '').trim() || null,
    default_priority: String(data.get('default_priority') ?? 'normal') as TicketPriority,
    default_category: String(data.get('default_category') ?? 'general') as TicketCategory,
    default_sla_type: defaultSlaType,
    default_sla_custom_days: defaultSlaType === 'custom' ? Number(data.get('default_sla_custom_days') ?? 14) : null,
    default_subject: String(data.get('default_subject') ?? '').trim() || null,
    default_description: String(data.get('default_description') ?? '').trim() || null,
    additional_information: String(data.get('additional_information') ?? '').trim() || null,
    allowed_roles: roles.length > 0 ? roles : ['all'],
    active: data.get('active') === 'on',
    sort_order: Number(data.get('sort_order') ?? 0),
    checklist_items: checklist
      .map((item, index) => ({
        position: index,
        text: item.text.trim(),
        comment_required_to_complete: item.comment_required_to_complete,
        file_required_to_complete: item.file_required_to_complete,
      }))
      .filter((item) => item.text.length > 0),
  }
}

export function TicketTypeEditorPage({ mode }: { mode: 'create' | 'edit' }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams()
  const { canManageTicketTypes } = usePermissions()
  const [error, setError] = useState<string | null>(null)

  const { data: ticketType, isLoading } = useQuery({
    queryKey: ['settings-ticket-type', id],
    queryFn: () => ticketTypeApi.get(id as string),
    enabled: mode === 'edit' && Boolean(id),
  })

  const [checklist, setChecklist] = useState<ChecklistDraft[]>([])
  const [roles, setRoles] = useState<Array<UserRole | 'all'>>(['all'])
  const [slaType, setSlaType] = useState<TicketSlaType>('1_day')

  useEffect(() => {
    if (!ticketType) return
    setChecklist(initialChecklist(ticketType))
    setRoles(ticketType.allowed_roles?.length ? ticketType.allowed_roles : ['all'])
    setSlaType(ticketType.default_sla_type)
  }, [ticketType])

  const saveMutation = useMutation({
    mutationFn: (payload: TicketTypePayload) => mode === 'edit' && id
      ? ticketTypeApi.update(id, payload)
      : ticketTypeApi.create(payload),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['settings-ticket-types'] })
      await queryClient.invalidateQueries({ queryKey: ['ticket-types'] })
      navigate('/settings/ticket-types')
    },
    onError: (mutationError) => {
      const errors = apiValidationErrors(mutationError)
      const first = errors ? Object.values(errors)[0] as string[] | undefined : undefined
      setError(first?.[0] ?? apiErrorMessage(mutationError, 'Could not save ticket type.'))
    },
  })

  if (!canManageTicketTypes()) {
    return <Navigate to="/settings" replace />
  }

  if (mode === 'edit' && isLoading) {
    return <p className="py-8 text-center text-sm text-gray-500">Loading ticket type...</p>
  }

  const current = ticketType
  const effectiveChecklist = checklist.length > 0 || current ? checklist : []
  const effectiveRoles = roles

  const setRole = (role: UserRole | 'all', checked: boolean) => {
    if (role === 'all') {
      setRoles(checked ? ['all'] : [])
      return
    }

    const withoutAll = effectiveRoles.filter((item) => item !== 'all')
    setRoles(checked ? [...withoutAll, role] : withoutAll.filter((item) => item !== role))
  }

  const moveItem = (index: number, direction: -1 | 1) => {
    const next = [...effectiveChecklist]
    const target = index + direction
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    setChecklist(next)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={mode === 'edit' ? 'Edit Ticket Type' : 'New Ticket Type'}
        subtitle="Configure defaults and internal checklist requirements for repeatable ticket work."
        action={(
          <Link to="/settings/ticket-types">
            <Button variant="secondary">Back to Ticket Types</Button>
          </Link>
        )}
      />

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <SurfaceCard>
        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault()
            saveMutation.mutate(payloadFromForm(event.currentTarget, effectiveChecklist, effectiveRoles))
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Input name="name" label="Name" required maxLength={150} defaultValue={current?.name ?? ''} />
            <Input name="type_group" label="Group" maxLength={80} list="ticket-type-groups" defaultValue={current?.type_group ?? ''} placeholder="Support, Onboarding, Equipment" />
            <datalist id="ticket-type-groups">
              <option value="Support" />
              <option value="Onboarding" />
              <option value="Equipment" />
              <option value="Billing" />
            </datalist>
          </div>

          <div className="grid gap-4 md:grid-cols-4">
            <label className="text-sm font-medium text-gray-700">
              Priority
              <select name="default_priority" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" defaultValue={current?.default_priority ?? 'normal'}>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              Category
              <select name="default_category" className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm" defaultValue={current?.default_category ?? 'general'}>
                <option value="billing">Billing</option>
                <option value="technical">Technical</option>
                <option value="onboarding">Onboarding</option>
                <option value="general">General</option>
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700">
              SLA
              <select
                name="default_sla_type"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={slaType}
                onChange={(event) => setSlaType(event.target.value as TicketSlaType)}
              >
                <option value="1_day">1 day</option>
                <option value="2_3_days">3 days</option>
                <option value="7_days">7 days</option>
                <option value="beginning_of_next_month">Beginning of next month</option>
                <option value="custom">Custom days</option>
              </select>
            </label>
            {slaType === 'custom' && (
              <Input name="default_sla_custom_days" label="Custom days" type="number" min="1" max="45" defaultValue={current?.default_sla_custom_days ?? 14} />
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Input name="default_subject" label="Default subject" maxLength={255} defaultValue={current?.default_subject ?? ''} />
            <Input name="sort_order" label="Sort order" type="number" defaultValue={current?.sort_order ?? 0} />
          </div>

          <label className="block text-sm font-medium text-gray-700">
            Default description
            <textarea name="default_description" className="mt-1 block min-h-[100px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm" defaultValue={current?.default_description ?? ''} />
          </label>

          <label className="block text-sm font-medium text-gray-700">
            Additional information
            <textarea name="additional_information" className="mt-1 block min-h-[90px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm" defaultValue={current?.additional_information ?? ''} />
          </label>

          <div>
            <div className="text-sm font-medium text-gray-700">Allowed roles</div>
            <div className="mt-2 flex flex-wrap gap-3">
              {ROLE_OPTIONS.map((role) => (
                <label key={role.value} className="flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={effectiveRoles.includes(role.value)}
                    onChange={(event) => setRole(role.value, event.target.checked)}
                  />
                  {role.label}
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input name="active" type="checkbox" defaultChecked={current?.active ?? true} />
            Active
          </label>

          <div>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Checklist</h3>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setChecklist([...effectiveChecklist, { text: '', comment_required_to_complete: false, file_required_to_complete: false }])}
              >
                + Add Item
              </Button>
            </div>
            <div className="mt-3 space-y-3">
              {effectiveChecklist.length === 0 ? (
                <p className="rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-500">No checklist items.</p>
              ) : effectiveChecklist.map((item, index) => (
                <div key={index} className="rounded-md border border-gray-200 p-3">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex gap-1 pt-6">
                      <Button type="button" variant="secondary" onClick={() => moveItem(index, -1)} disabled={index === 0}>Up</Button>
                      <Button type="button" variant="secondary" onClick={() => moveItem(index, 1)} disabled={index === effectiveChecklist.length - 1}>Down</Button>
                    </div>
                    <div className="min-w-[240px] flex-1">
                      <Input
                        label={`Item ${index + 1}`}
                        maxLength={500}
                        value={item.text}
                        onChange={(event) => {
                          const next = [...effectiveChecklist]
                          next[index] = { ...item, text: event.target.value }
                          setChecklist(next)
                        }}
                      />
                    </div>
                    <div className="space-y-2 pt-6 text-sm text-gray-700">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={item.comment_required_to_complete}
                          onChange={(event) => {
                            const next = [...effectiveChecklist]
                            next[index] = { ...item, comment_required_to_complete: event.target.checked }
                            setChecklist(next)
                          }}
                        />
                        Comment required
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={item.file_required_to_complete}
                          onChange={(event) => {
                            const next = [...effectiveChecklist]
                            next[index] = { ...item, file_required_to_complete: event.target.checked }
                            setChecklist(next)
                          }}
                        />
                        File required
                      </label>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setChecklist(effectiveChecklist.filter((_, itemIndex) => itemIndex !== index))}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <Link to="/settings/ticket-types">
              <Button type="button" variant="secondary">Cancel</Button>
            </Link>
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : 'Save Ticket Type'}
            </Button>
          </div>
        </form>
      </SurfaceCard>
    </div>
  )
}
